/**
 * Fija la contraseña nueva a partir del enlace enviado por `ForgotPassword.tsx`
 * (`?token=opr_...`). El token se valida recién al enviar `POST /operators/reset` — a diferencia
 * de `/account/reset` en el IdP, esta es una SPA sin render server-side, así que no hay una
 * comprobación previa del token (ver `platform/operator-tokens.ts`).
 */
import { useState, type FormEvent, type ReactElement } from "react";
import { Link, useSearchParams } from "react-router-dom";

import { resetOperatorPassword } from "../api/passwordReset";
import { Field } from "../components/Field";
import { toDisplayMessage } from "../lib/errors";

export function ResetPassword(): ReactElement {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") ?? "";

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setError(null);
    if (password !== confirmPassword) {
      setError("Las contraseñas no coinciden.");
      return;
    }
    setSubmitting(true);
    try {
      await resetOperatorPassword({ token, password });
      setDone(true);
    } catch (err) {
      setError(toDisplayMessage(err));
    } finally {
      setSubmitting(false);
    }
  }

  if (!token) {
    return (
      <div className="login-shell">
        <div className="login-card">
          <h1>Enlace inválido</h1>
          <p className="login-subtitle">Este enlace no trae un token de recuperación. Pide uno nuevo.</p>
          <Link className="login-forgot-link" to="/forgot-password">
            Pedir un enlace nuevo
          </Link>
        </div>
      </div>
    );
  }

  if (done) {
    return (
      <div className="login-shell">
        <div className="login-card">
          <h1>Contraseña actualizada</h1>
          <p className="login-subtitle">Ya puedes iniciar sesión con tu contraseña nueva.</p>
          <Link className="login-forgot-link" to="/login">
            Ir a iniciar sesión
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="login-shell">
      <form className="login-card" onSubmit={(event) => void handleSubmit(event)} noValidate>
        <h1>Elige una contraseña nueva</h1>

        <Field label="Contraseña nueva" hint="Al menos 10 caracteres.">
          {(props) => (
            <input
              {...props}
              type="password"
              autoComplete="new-password"
              required
              minLength={10}
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />
          )}
        </Field>

        <Field label="Confirma la contraseña">
          {(props) => (
            <input
              {...props}
              type="password"
              autoComplete="new-password"
              required
              minLength={10}
              value={confirmPassword}
              onChange={(event) => setConfirmPassword(event.target.value)}
            />
          )}
        </Field>

        {error && (
          <p className="field-error" role="alert">
            {error}
          </p>
        )}

        <button type="submit" className="button-primary button-block" disabled={submitting}>
          {submitting ? "Guardando…" : "Guardar contraseña"}
        </button>
      </form>
    </div>
  );
}
